import { forwardRef, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/cn";

/**
 * Number input that doesn't trap the user behind a leading zero.
 *
 * Problem with the naive controlled `<input type="number" value={n}>`:
 * when `n === 0`, the input shows "0". Pressing Backspace clears it,
 * `onChange` fires with `""`, `Number("") === 0` re-renders to "0", and
 * the user can never delete the digit. They have to put the cursor to
 * the left of the zero and type — annoying and unintuitive.
 *
 * Fix: hold the displayed string in local state. While focused, the
 * input shows whatever the user typed (including empty, "-", "1."),
 * and we only push parsed numeric updates to the parent when the
 * string parses to a finite number. On blur we snap to a canonical
 * string of the committed value. This gives a normal "select the 0,
 * type 5, get 5" feel while still emitting numbers upstream.
 *
 * Range/sign: pass `min` / `max` to clamp on commit. Set `allowSigned`
 * to permit `-`. `step` is forwarded to the underlying input for the
 * native up/down spinners.
 */
export interface NumberFieldProps {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  step?: number;
  allowSigned?: boolean;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Aria-label / accessible name. */
  "aria-label"?: string;
  /** Fire onChange on every parseable keystroke (default) or only on
   *  blur. Keystroke mode keeps a live preview while the user types;
   *  blur mode is useful when each keystroke would trigger heavy work
   *  (e.g. a full schedule recompute). */
  commit?: "live" | "blur";
}

export const NumberField = forwardRef<HTMLInputElement, NumberFieldProps>(function NumberField(
  {
    value,
    onChange,
    min,
    max,
    step,
    allowSigned = false,
    className,
    placeholder,
    disabled,
    commit = "live",
    ...rest
  },
  ref,
) {
  // Local string state. `value` prop syncs in only when we're not
  // focused (otherwise we'd clobber the user's transient input mid-type).
  const [text, setText] = useState(() => formatNumber(value));
  const focusedRef = useRef(false);
  useEffect(() => {
    if (!focusedRef.current) setText(formatNumber(value));
  }, [value]);

  const clamp = (n: number): number => {
    let out = n;
    if (typeof min === "number" && out < min) out = min;
    if (typeof max === "number" && out > max) out = max;
    return out;
  };

  return (
    <input
      ref={ref}
      type="text"
      inputMode={allowSigned ? "decimal" : "numeric"}
      pattern={allowSigned ? "-?[0-9]*\\.?[0-9]*" : "[0-9]*\\.?[0-9]*"}
      value={text}
      placeholder={placeholder}
      disabled={disabled}
      className={cn(
        "rounded-md border border-edge bg-surface px-2 py-1.5 text-sm",
        "focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/40",
        disabled && "opacity-60",
        className,
      )}
      step={step}
      onFocus={(e) => {
        focusedRef.current = true;
        // Select-all on focus so the operator can just type to replace
        // — matches the muscle memory of a native number spinner.
        e.currentTarget.select();
      }}
      onChange={(e) => {
        let next = e.target.value;
        // Filter to allowed character set as the user types so a paste
        // of "abc" doesn't end up in the input. Keep partial strings
        // ("", "-", "1.") since they're valid transient states.
        const pattern = allowSigned ? /^-?[0-9]*\.?[0-9]*$/ : /^[0-9]*\.?[0-9]*$/;
        if (!pattern.test(next)) {
          next = text;
        }
        setText(next);
        if (commit === "live" && next !== "" && next !== "-" && next !== ".") {
          const parsed = Number(next);
          if (Number.isFinite(parsed)) {
            const clamped = clamp(parsed);
            if (clamped !== value) onChange(clamped);
          }
        }
      }}
      onBlur={() => {
        focusedRef.current = false;
        const parsed = text === "" || text === "-" || text === "." ? value : Number(text);
        if (Number.isFinite(parsed)) {
          const clamped = clamp(parsed);
          if (clamped !== value) onChange(clamped);
          setText(formatNumber(clamped));
        } else {
          // Unparseable — revert.
          setText(formatNumber(value));
        }
      }}
      onKeyDown={(e) => {
        // Enter commits the value (same as blur), letting the operator
        // keep typing in another field without leaving the keyboard.
        if (e.key === "Enter") {
          (e.currentTarget as HTMLInputElement).blur();
        }
      }}
      {...rest}
    />
  );
});

function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return "";
  // Use plain toString so integers render without a trailing ".0" and
  // floats keep their precision.
  return String(n);
}
